import {
    createContext,
    useState,
    useEffect,
    useLayoutEffect,
    useRef,
    useMemo,
    useCallback,
    type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import {
    buildBlueskyAccount,
    buildExpiredBlueskyAccount,
    buildMastodonAccount,
    buildPixelfedAccount,
    buildNostrAccount,
} from '@/lib/accountUtils'
import type { ConnectedAccount } from '@/types/account'

/** Platform identifier — the specific service/app a user is connecting to. */
export type Platform = 'bluesky' | 'mastodon' | 'pixelfed' | 'nostr'

/** @deprecated Use Platform */
export type Protocol = Platform

const STORAGE_KEY = 'mackinac:selectedPlatform'
const LEGACY_STORAGE_KEY = 'mackinac:selectedProtocol'
const ACTIVE_ACCOUNT_STORAGE_KEY = 'mackinac:activeAccountId'
import { PENDING_ACTIVE_ACCOUNT_KEY } from '@/constants/storage'

function getStoredPlatform(): Platform {
    try {
        // Check new key first, fall back to legacy key for migration
        const stored = localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_STORAGE_KEY)
        if (
            stored === 'bluesky' ||
            stored === 'mastodon' ||
            stored === 'pixelfed' ||
            stored === 'nostr'
        )
            return stored
    } catch {
        // localStorage unavailable (privacy mode, SSR, etc.)
    }
    return 'bluesky'
}

function consumePendingActiveAccount(): string | null {
    try {
        const pending = localStorage.getItem(PENDING_ACTIVE_ACCOUNT_KEY)
        if (pending) {
            localStorage.removeItem(PENDING_ACTIVE_ACCOUNT_KEY)
            localStorage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, pending)
            return pending
        }
    } catch {
        // localStorage unavailable
    }
    return null
}

function getStoredActiveAccountId(): string | null {
    try {
        return localStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY)
    } catch {
        return null
    }
}

export interface PlatformContextType {
    selectedProtocol: Platform
    setSelectedProtocol: (platform: Platform) => void
    availableProtocols: Platform[]
    /** Whether cross-protocol features should be available.
     *  Requires at least one Bluesky session and at least one fediverse session. */
    canCrossProtocol: boolean
    connectedAccounts: ConnectedAccount[]
    activeAccount: ConnectedAccount | null
    setActiveAccount: (accountId: string) => void
}

/** @deprecated Use PlatformContextType */
export type ProtocolContextType = PlatformContextType

export const PlatformContext = createContext<PlatformContextType | undefined>(undefined)

/** @deprecated Use PlatformContext */
export const ProtocolContext = PlatformContext

export function PlatformProvider({ children }: { children: ReactNode }) {
    const {
        sessions,
        expiredAccounts,
        blueskyDidOrder,
        mastodonSessions,
        pixelfedSession,
        nostrSession,
        session,
        mastodonSession,
        isLoading,
        setActiveBlueskyDid,
        setActiveMastodonId,
    } = useAuth()
    const [activeAccountId, setActiveAccountIdState] = useState<string | null>(
        () => consumePendingActiveAccount() ?? getStoredActiveAccountId()
    )

    // Build connected accounts from all sessions. Bluesky accounts (live and
    // expired) are emitted in stored-DID order so an account keeps its position
    // across expiry and reconnect.
    const connectedAccounts = useMemo(() => {
        const accounts: ConnectedAccount[] = []
        const liveByDid = new Map(sessions.map(s => [s.did as string, s]))
        const expiredByDid = new Map(expiredAccounts.map(a => [a.did, a]))
        for (const did of blueskyDidOrder) {
            const live = liveByDid.get(did)
            if (live) {
                accounts.push(buildBlueskyAccount(live))
                continue
            }
            const expired = expiredByDid.get(did)
            // A DID that is neither is unresolved (transient restore failure) — skip it
            if (expired) accounts.push(buildExpiredBlueskyAccount(expired))
        }
        for (const s of mastodonSessions) {
            accounts.push(buildMastodonAccount(s))
        }
        if (pixelfedSession) accounts.push(buildPixelfedAccount(pixelfedSession))
        if (nostrSession) accounts.push(buildNostrAccount(nostrSession))
        return accounts
    }, [
        sessions,
        expiredAccounts,
        blueskyDidOrder,
        mastodonSessions,
        pixelfedSession,
        nostrSession,
    ])

    // Resolve active account from ID, with migration fallback.
    // An expired placeholder is never the active account.
    const activeAccount = useMemo(() => {
        // Try to find by stored ID
        if (activeAccountId) {
            const found = connectedAccounts.find(a => a.id === activeAccountId)
            if (found && found.status !== 'expired') return found
        }

        // Migration fallback: use stored platform preference to pick an account
        const storedPlatform = getStoredPlatform()
        const byPlatform = connectedAccounts.find(
            a => a.status !== 'expired' && a.platform === storedPlatform
        )
        if (byPlatform) return byPlatform

        // Last resort: first available non-expired account
        return connectedAccounts.find(a => a.status !== 'expired') ?? null
    }, [activeAccountId, connectedAccounts])

    // Derive selectedProtocol from active account
    const selectedProtocol = useMemo(
        () => activeAccount?.platform ?? getStoredPlatform(),
        [activeAccount]
    )

    // Determine available protocols based on sessions
    const availableProtocols = useMemo(() => {
        const platforms: Platform[] = []
        if (sessions.length > 0) platforms.push('bluesky')
        if (mastodonSessions.length > 0) platforms.push('mastodon')
        if (pixelfedSession) platforms.push('pixelfed')
        if (nostrSession) platforms.push('nostr')
        return platforms
    }, [sessions, mastodonSessions, pixelfedSession, nostrSession])

    const canCrossProtocol = useMemo(
        () => sessions.length >= 1 && (mastodonSessions.length > 0 || !!pixelfedSession),
        [sessions, mastodonSessions, pixelfedSession]
    )

    const selectedMastodonAccountId = useMemo(
        () => (mastodonSession ? buildMastodonAccount(mastodonSession).id : null),
        [mastodonSession]
    )

    // The account the app is actually acting as: AuthContext's selection for the active platform.
    const actingAccountId = useMemo(() => {
        if (!activeAccount) return null
        if (activeAccount.platform === 'bluesky') return session?.did ?? null
        if (activeAccount.platform === 'mastodon') return selectedMastodonAccountId
        return activeAccount.id
    }, [activeAccount, session, selectedMastodonAccountId])

    // Persist active account and platform to localStorage, and update AuthContext selections
    const setActiveAccount = useCallback(
        (accountId: string) => {
            const account = connectedAccounts.find(a => a.id === accountId)
            // An expired placeholder is reconnected, never switched to
            if (!account || account.status === 'expired') return

            try {
                localStorage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, accountId)
                localStorage.setItem(STORAGE_KEY, account.platform)
                // Remove legacy key on next write to complete the migration
                localStorage.removeItem(LEGACY_STORAGE_KEY)
            } catch {
                // localStorage unavailable
            }
            setActiveAccountIdState(accountId)

            // Update the AuthContext active session for the appropriate platform
            if (account.platform === 'bluesky') {
                setActiveBlueskyDid(accountId)
            } else if (account.platform === 'mastodon') {
                setActiveMastodonId(accountId)
            }
            // Pixelfed only has one session, no selection needed
        },
        [connectedAccounts, setActiveBlueskyDid, setActiveMastodonId]
    )

    // Backward-compatible setSelectedProtocol: finds first account matching that platform
    const setSelectedProtocol = useCallback(
        (platform: Platform) => {
            const account = connectedAccounts.find(
                a => a.status !== 'expired' && a.platform === platform
            )
            if (account) {
                setActiveAccount(account.id)
            }
        },
        [connectedAccounts, setActiveAccount]
    )

    const queryClient = useQueryClient()

    // Reconcile AuthContext to a *new* stored active account (#2458), and fall back when the stored
    // id is invalid. Two refs: `requested` stops the setter being re-issued for the same id while
    // React applies it (also under StrictMode's double-invoke); `reconciled` is only recorded once
    // the acting account is *observed* to equal the active id — never merely because the setter was
    // called. Once-per-value, not continuous: AuthContext legitimately moves its own selection ahead
    // of an expiry (commitExpiry, AuthContext.tsx) while activeAccountId is still valid for
    // one render; re-asserting activeAccountId there would re-select the expiring account. The
    // fallback branch brings activeAccountId along once the account is marked expired. Layout
    // effect so the flip is flushed before paint.
    const reconciledActiveIdRef = useRef<string | null>(null)
    const requestedActiveIdRef = useRef<string | null>(null)
    useLayoutEffect(() => {
        if (isLoading || connectedAccounts.length === 0) return

        const active = activeAccountId
            ? connectedAccounts.find(a => a.id === activeAccountId && a.status !== 'expired')
            : undefined

        if (active) {
            if (reconciledActiveIdRef.current === active.id) return // confirmed in sync for this id
            if (actingAccountId === active.id) {
                // observed: AuthContext acts as it
                reconciledActiveIdRef.current = active.id
                requestedActiveIdRef.current = null
                return
            }
            if (requestedActiveIdRef.current === active.id) return // setter already issued; wait for it
            requestedActiveIdRef.current = active.id
            if (active.platform === 'bluesky') {
                setActiveBlueskyDid(active.id)
            } else if (active.platform === 'mastodon') {
                setActiveMastodonId(active.id)
            }
            return
        }

        // Active account is invalid - fall back to the first non-expired account
        const fallback = connectedAccounts.find(a => a.status !== 'expired')
        if (fallback) setActiveAccount(fallback.id)
    }, [
        isLoading,
        connectedAccounts,
        activeAccountId,
        actingAccountId,
        setActiveAccount,
        setActiveBlueskyDid,
        setActiveMastodonId,
    ])

    // Single transition coordinator — replaces the previous separate platform-switch and
    // account-change cache-clearing effects. It observes ONE tuple, { activeAccount.platform,
    // activeAccount.id }, and only in a render where the acting account already equals
    // activeAccount.id (settled). `committedRef` is seeded from the ACTING account in the first
    // render where it is known (on a divergent mount that is the pre-reconciliation account —
    // #2458) and then advances only when a transition's work runs, so a switch that lands as
    // "platform changed" in one render and "account changed" in the next is seen once, at
    // settlement, as the union of both — never as two transitions — and a divergent mount's
    // settlement is a real transition. Because it waits for settlement, the invalidations below
    // always refetch with the new account's closure. With account-scoped keys none of this is
    // load-bearing for isolation; it refreshes engagement fields on shared content caches and
    // drops stale pages.
    type CommittedAccount = { platform: Platform; id: string }
    const committedRef = useRef<CommittedAccount | null>(null)
    useEffect(() => {
        if (!activeAccount) return // nothing to act as
        if (actingAccountId === null) return // acting account not known yet
        // Seed from the account the app is ACTING AS, not the stored active account. On a
        // divergent mount (add-account, or already-divergent persisted state) this render has
        // activeAccount pointing at the new account while the acting id is still the old one,
        // so seeding the acting id makes the settlement below a real transition instead of a
        // free initialisation. On an aligned mount the seed equals the settled tuple and
        // nothing fires.
        committedRef.current ??= { platform: activeAccount.platform, id: actingAccountId }
        if (actingAccountId !== activeAccount.id) return // split render — not settled yet
        const next: CommittedAccount = { platform: activeAccount.platform, id: activeAccount.id }
        const prev = committedRef.current
        const platformChanged = prev.platform !== next.platform
        const accountChanged = prev.id !== next.id
        if (!platformChanged && !accountChanged) return // settled, nothing moved
        committedRef.current = next // advance only when work runs

        // Common to both kinds of transition — once, even when both happen in the same settlement
        queryClient.removeQueries({ queryKey: ['timeline'] })
        queryClient.removeQueries({ queryKey: ['newPostsCheck'] })

        if (platformChanged) {
            // Remove AT Protocol-only caches when leaving Bluesky
            if (prev.platform === 'bluesky') {
                queryClient.removeQueries({ queryKey: ['subscriptions'] })
                queryClient.removeQueries({ queryKey: ['articlesFeed'] })
                queryClient.removeQueries({ queryKey: ['actorArticles'] })
                queryClient.removeQueries({ queryKey: ['customVerification'] })
            }
            // Remove protocol-specific social graph caches on any platform switch
            queryClient.removeQueries({ queryKey: ['followers'] })
            queryClient.removeQueries({ queryKey: ['following'] })
            queryClient.removeQueries({ queryKey: ['ap-followers'] })
            queryClient.removeQueries({ queryKey: ['ap-following'] })
        }
        if (accountChanged) {
            // repostUri/likeUri embedded in cached posts belong to the previous account and must
            // not carry over when the user switches between two accounts on the same platform.
            void queryClient.invalidateQueries({ queryKey: ['posts'] })
            void queryClient.invalidateQueries({ queryKey: ['thread'] })
            void queryClient.invalidateQueries({ queryKey: ['hashtag'] })
        }
    }, [activeAccount, actingAccountId, queryClient])

    const value = useMemo(
        () => ({
            selectedProtocol,
            setSelectedProtocol,
            availableProtocols,
            canCrossProtocol,
            connectedAccounts,
            activeAccount,
            setActiveAccount,
        }),
        [
            selectedProtocol,
            setSelectedProtocol,
            availableProtocols,
            canCrossProtocol,
            connectedAccounts,
            activeAccount,
            setActiveAccount,
        ]
    )

    return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>
}

/** @deprecated Use PlatformProvider */
export const ProtocolProvider = PlatformProvider
